import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, IconButton, InputAdornment, Paper, Snackbar,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import { Search as SearchIcon, Visibility as ViewIcon, PointOfSale as CashierIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoyaltyService from '../../services/loyalty.service';
import { useTranslation } from '../../hooks/useTranslation';
import { apiError, formatDate, formatMoney, formatPoints } from './loyaltyUtils';

const LoyaltyClientList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, per_page: 25, current_page: 1 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await LoyaltyService.getClients({ search, page: page + 1, per_page: perPage });
      setRows(res.data?.data || []);
      setMeta(res.data?.meta || { total: 0 });
    } catch (error) {
      setSnackbar({ open: true, message: apiError(error, t('loyalty.errorLoading')), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [search, page, perPage, t]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" fontWeight="bold">{t('loyalty.members')}</Typography>
        <Button variant="contained" startIcon={<CashierIcon />} onClick={() => navigate('/loyalty')}>{t('loyalty.cashierTitle')}</Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder={t('loyalty.searchMembers')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.phone')}</TableCell>
                <TableCell align="right">{t('loyalty.points')}</TableCell>
                <TableCell align="right">{t('loyalty.lifetimePoints')}</TableCell>
                <TableCell align="right">{t('loyalty.totalSpent')}</TableCell>
                <TableCell align="right">{t('loyalty.visits')}</TableCell>
                <TableCell>{t('loyalty.lastVisit')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">{t('loyalty.noMembers')}</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.uuid} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/loyalty/clients/${r.uuid}`)}>
                  <TableCell>
                    <Typography fontWeight="bold">{r.full_name}</Typography>
                    {r.tenant_client && <Typography variant="caption" color="text.secondary">{t('nav.clients')}: {r.tenant_client.name}</Typography>}
                  </TableCell>
                  <TableCell>{r.phone}</TableCell>
                  <TableCell align="right"><b>{formatPoints(r.points_balance)}</b></TableCell>
                  <TableCell align="right">{formatPoints(r.lifetime_points)}</TableCell>
                  <TableCell align="right">{formatMoney(r.total_spent)}</TableCell>
                  <TableCell align="right">{r.visit_count}</TableCell>
                  <TableCell>{formatDate(r.last_visit_at)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/loyalty/clients/${r.uuid}`); }}><ViewIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={meta.total || 0}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LoyaltyClientList;
